import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Button, Typography, TextField, MenuItem,
  CircularProgress, Alert,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { format } from 'date-fns';

const NHS_BLUE = '#005EB8';
const AM_BG    = '#E3F2FD';
const PM_BG    = '#FFF3E0';

export default function DailySchedulePrint() {
  const [date,     setDate]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clinicId, setClinicId] = useState('');
  const [clinics,  setClinics]  = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [loaded,   setLoaded]   = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/clinics', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const sorted = [...r.data].sort((a, b) => {
          const nA = parseInt((a.clinic_name.match(/\d+/) || [0])[0]);
          const nB = parseInt((b.clinic_name.match(/\d+/) || [0])[0]);
          return nA !== nB ? nA - nB : a.clinic_name.localeCompare(b.clinic_name);
        });
        setClinics(sorted);
      })
      .catch(() => {});
  }, []);

  const load = async () => {
    if (!clinicId) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/bookings', {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: date, end_date: date, clinic_id: clinicId, status: 'confirmed' },
      });
      setBookings(data);
      setLoaded(true);
    } catch {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Build room grid
  const roomOrder = [];
  const roomMap = {};
  for (const b of bookings) {
    if (!roomMap[b.room_name]) {
      roomMap[b.room_name] = { am: null, pm: null, all_day: null, number: b.room_number };
      roomOrder.push(b.room_name);
    }
    roomMap[b.room_name][b.session] = b;
  }
  const rooms = [...new Set(roomOrder)].sort((a, b) => {
    const nA = parseInt((roomMap[a].number?.match(/\d+/) || [0])[0]);
    const nB = parseInt((roomMap[b].number?.match(/\d+/) || [0])[0]);
    return nA !== nB ? nA - nB : a.localeCompare(b);
  });

  const clinicName = clinics.find(c => String(c.id) === String(clinicId))?.clinic_name || '';
  const dateLabel  = date ? format(new Date(date + 'T12:00:00'), 'EEEE d MMMM yyyy') : '';
  const printedAt  = format(new Date(), 'dd/MM/yyyy HH:mm');

  const bookingCellHtml = (booking, session) => {
    if (!booking) return `<td style="border:1px solid #ccc;padding:6px 8px;background:#fafafa;width:45%"></td>`;
    const bg = booking.color ? `${booking.color}18` : (session === 'am' ? AM_BG : PM_BG);
    return `<td style="border:1px solid #ccc;padding:6px 8px;vertical-align:top;background:${bg};width:45%">
      <div style="font-weight:bold;font-size:11px;margin-bottom:2px">${booking.specialty || '—'}</div>
      ${booking.doctor_name ? `<div style="font-size:10px;color:#333">${booking.doctor_name}</div>` : ''}
      ${booking.clinic_code ? `<div style="font-size:10px;color:#666;margin-top:1px">${booking.clinic_code}</div>` : ''}
    </td>`;
  };

  const buildPrintHtml = () => {
    if (!rooms.length) return '';

    const rowsHtml = rooms.map(room => {
      const am  = roomMap[room].am;
      const pm  = roomMap[room].pm;
      const all = roomMap[room].all_day;
      const roomCell = `<td style="border:1px solid #ccc;padding:6px 8px;font-weight:bold;font-size:11px;background:#f5f5f5;vertical-align:middle;width:10%;white-space:nowrap">${room}</td>`;

      if (all) {
        const bg = all.color ? `${all.color}18` : '#f3e5f5';
        return `<tr>
          ${roomCell}
          <td colspan="2" style="border:1px solid #ccc;padding:6px 8px;vertical-align:top;background:${bg}">
            <span style="font-size:9px;color:#888;font-style:italic">All Day (08:30–17:30)</span><br/>
            <div style="font-weight:bold;font-size:11px;margin-top:2px">${all.specialty || '—'}</div>
            ${all.doctor_name ? `<div style="font-size:10px;color:#333">${all.doctor_name}</div>` : ''}
            ${all.clinic_code ? `<div style="font-size:10px;color:#666">${all.clinic_code}</div>` : ''}
          </td>
        </tr>`;
      }
      return `<tr>${roomCell}${bookingCellHtml(am, 'am')}${bookingCellHtml(pm, 'pm')}</tr>`;
    }).join('');

    return `
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:8px">
        <div>
          <div style="font-size:16px;font-weight:bold;color:${NHS_BLUE}">${clinicName}</div>
          <div style="font-size:12px;color:#555;margin-top:2px">${dateLabel}</div>
        </div>
        <div style="font-size:8px;color:#999;text-align:right">Cambridge University Hospitals<br/>Room Booking System</div>
      </div>
      <table style="border-collapse:collapse;width:100%">
        <thead>
          <tr>
            <th style="border:1px solid #ccc;padding:6px 8px;background:${NHS_BLUE};color:white;text-align:left;width:10%">Room</th>
            <th style="border:1px solid #ccc;padding:6px 8px;background:${AM_BG};color:#8B5CC8;text-align:center;width:45%">AM &nbsp; 08:30–12:30</th>
            <th style="border:1px solid #ccc;padding:6px 8px;background:${PM_BG};color:#E65100;text-align:center;width:45%">PM &nbsp; 13:30–17:30</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div style="margin-top:6px;font-size:8px;color:#999;text-align:right">Printed ${printedAt}</div>
    `;
  };

  const handlePrint = () => {
    if (!rooms.length) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>${clinicName} — ${dateLabel}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:Arial,sans-serif;font-size:11px}
        @page{size:A4 portrait;margin:10mm}
        thead{display:table-header-group}
        tr{page-break-inside:avoid}
      </style>
    </head><body>${buildPrintHtml()}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  // Screen preview cell
  const CellPreview = ({ booking, session }) => {
    if (!booking) return (
      <td style={{ border: '1px solid #ddd', padding: '5px 7px', background: '#fafafa', width: '45%' }} />
    );
    const bg = booking.color ? `${booking.color}18` : (session === 'am' ? AM_BG : PM_BG);
    return (
      <td style={{ border: '1px solid #ddd', padding: '5px 7px', verticalAlign: 'top', background: bg, width: '45%' }}>
        <div style={{ fontWeight: 'bold', fontSize: 12 }}>{booking.specialty || '—'}</div>
        {booking.doctor_name && <div style={{ fontSize: 11, color: '#333', marginTop: 1 }}>{booking.doctor_name}</div>}
        {booking.clinic_code && <div style={{ fontSize: 11, color: '#666' }}>{booking.clinic_code}</div>}
      </td>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Daily Room Schedule</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generates a printable A4 portrait schedule — rooms listed down the left, AM and PM across the top. Prints across as many pages as needed.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Date"
          type="date"
          size="small"
          value={date}
          onChange={e => { setDate(e.target.value); setLoaded(false); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <TextField
          select
          label="Clinic"
          size="small"
          value={clinicId}
          onChange={e => { setClinicId(e.target.value); setLoaded(false); }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">— Select clinic —</MenuItem>
          {clinics.map(c => (
            <MenuItem key={c.id} value={c.id}>{c.clinic_name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={load} disabled={!clinicId || loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
        </Button>
        {loaded && rooms.length > 0 && (
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
            Print
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loaded && rooms.length === 0 && (
        <Alert severity="info">No confirmed bookings found for {clinicName} on {dateLabel}.</Alert>
      )}

      {/* Screen preview */}
      {loaded && rooms.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">{clinicName}</Typography>
              <Typography variant="body2" color="text.secondary">{dateLabel}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {bookings.length} booking{bookings.length !== 1 ? 's' : ''} · {rooms.length} room{rooms.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <Box sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', background: NHS_BLUE, color: 'white', textAlign: 'left', width: '10%', fontSize: 12 }}>Room</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', background: AM_BG, color: '#8B5CC8', textAlign: 'center', width: '45%', fontSize: 12 }}>AM &nbsp; 08:30–12:30</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', background: PM_BG, color: '#E65100', textAlign: 'center', width: '45%', fontSize: 12 }}>PM &nbsp; 13:30–17:30</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map(room => {
                  const all = roomMap[room].all_day;
                  const roomCell = (
                    <td style={{ border: '1px solid #ccc', padding: '6px 8px', fontWeight: 'bold', fontSize: 12, background: '#f5f5f5', verticalAlign: 'middle', whiteSpace: 'nowrap', width: '10%' }}>
                      {room}
                    </td>
                  );

                  if (all) {
                    const bg = all.color ? `${all.color}18` : '#f3e5f5';
                    return (
                      <tr key={room}>
                        {roomCell}
                        <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 8px', verticalAlign: 'top', background: bg }}>
                          <div style={{ fontSize: 10, color: '#888', fontStyle: 'italic', marginBottom: 2 }}>All Day (08:30–17:30)</div>
                          <div style={{ fontWeight: 'bold', fontSize: 12 }}>{all.specialty || '—'}</div>
                          {all.doctor_name && <div style={{ fontSize: 11, color: '#333' }}>{all.doctor_name}</div>}
                          {all.clinic_code && <div style={{ fontSize: 11, color: '#666' }}>{all.clinic_code}</div>}
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={room}>
                      {roomCell}
                      <CellPreview booking={roomMap[room].am} session="am" />
                      <CellPreview booking={roomMap[room].pm} session="pm" />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
            Preview · click Print to open the print-ready version
          </Typography>
        </Box>
      )}
    </Box>
  );
}
